/*
 * As with the Modal, the panel is a child of the scrim so that the target
 * check in `scrimPointerDown` is exact. The scrim only has to push the panel
 * to one edge, which `justify-content` does without either element needing to
 * be positioned.
 */

.scrim {
  position: fixed;
  inset: 0;
  z-index: var(--z-sheet);
  display: flex;
  background: var(--scrim);
  animation: scrim-in var(--dur-fast) var(--ease-out);
}

.scrim[data-side='right'] {
  justify-content: flex-end;
}

.scrim[data-side='left'] {
  justify-content: flex-start;
}

@keyframes scrim-in {
  from {
    opacity: 0;
  }
}

.panel {
  /* Containing block for the bare header's floating close control below. */
  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  width: 100%;
  height: 100%;
  background: var(--surface-raised);
  color: var(--text);
  box-shadow: var(--shadow-4);
}

.panel[data-side='right'] {
  border-left: 1px solid var(--rule);
  animation: slide-from-right var(--dur-med) var(--ease-out);
}

.panel[data-side='left'] {
  border-right: 1px solid var(--rule);
  animation: slide-from-left var(--dur-med) var(--ease-out);
}

/*
 * A percentage of the panel's own width rather than a fixed distance, so the
 * sheet always starts exactly off screen whatever `size` it was given. A fixed
 * translate leaves a wide drawer partly visible for the first frame, which
 * reads as a flash of the record before it opens.
 */
@keyframes slide-from-right {
  from {
    transform: translateX(100%);
  }
}

@keyframes slide-from-left {
  from {
    transform: translateX(-100%);
  }
}

/*
 * Widths, expressed as a max so a narrow window gets the whole viewport rather
 * than a sliver of page behind a sheet it cannot read. On a tablet in the
 * field this is the common case, not the edge case.
 */
.sm {
  max-width: min(20rem, 92vw);
}

.md {
  max-width: min(30rem, 96vw);
}

.lg {
  max-width: min(46rem, 96vw);
}

.header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--rule);
}

/*
 * No heading of its own: the content brought one. The border and the padding
 * would then draw a rule under nothing, so the header collapses to just the
 * close control, floating over the top corner of whatever was passed in.
 */
.header[data-bare] {
  position: absolute;
  top: var(--space-2);
  right: var(--space-2);
  padding: 0;
  border-bottom: 0;
}

.headings {
  display: grid;
  gap: var(--space-1);
  min-width: 0;
}

.title {
  font: var(--type-page);
  color: var(--text);
}

.description {
  font: var(--type-body-sm);
  color: var(--text-muted);
}

.body {
  padding: var(--space-5);
  overflow-y: auto;
  overscroll-behavior: contain;
}

/*
 * With no header row, the body owns the whole panel and its own padding is
 * whatever the content wants. The shell's navigation sets its own.
 */
.header[data-bare] + .body {
  padding: 0;
}

.footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-5);
  border-top: 1px solid var(--rule);
}
